import React, { useState, useEffect, useMemo } from "react";
import Select from "../../Select";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Label,
} from "recharts";

const NativeAnalysis = ({ content }) => {
  if (!content || typeof content !== "string") return null;

  const parseInline = (text) => {
    const parts = text.split(/(\*\*.*?\*\*|_.*?_)/g);
    return parts.map((part, index) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return (
          <strong key={index} className="font-bold text-[#323334]">
            {part.slice(2, -2)}
          </strong>
        );
      } else if (part.startsWith("_") && part.endsWith("_")) {
        return (
          <em key={index} className="italic text-[#323334]">
            {part.slice(1, -1)}
          </em>
        );
      }
      return part;
    });
  };

  return (
    <div className="font-sans text-[#7E8081] space-y-2 text-[15px] leading-[19.5px]">
      {content.split("\n").map((line, index) => {
        const trimmed = line.trim();
        if (!trimmed) return <div key={index} className="h-1" />;

        if (trimmed.startsWith("### ")) {
          return (
            <h3
              key={index}
              className="text-[16px] font-bold text-[#323334] mt-2 mb-1"
            >
              {parseInline(trimmed.slice(4))}
            </h3>
          );
        }

        if (trimmed.startsWith("- ") || trimmed.startsWith("* ")) {
          return (
            <div key={index} className="flex items-start ml-2 mb-1">
              <span className="mr-2 text-[#59AD31] text-lg leading-none">•</span>
              <span>{parseInline(trimmed.slice(2))}</span>
            </div>
          );
        }

        return (
          <p key={index} className="mb-2">
            {parseInline(trimmed)}
          </p>
        );
      })}
    </div>
  );
};

const xAxisLabelYear = "Years";
const yAxisLabelUsd = "Million USD";
const yAxisDxUsd = -yAxisLabelUsd.length;

const LoadingAnalysisHelper = () => (
  <div className="flex items-center gap-2 text-[#7E8081] text-sm md:text-base animate-pulse">
    <svg
      className="animate-spin h-5 w-5 text-[#59AD31]"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      ></circle>
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      ></path>
    </svg>
    <span className="font-bold">Generating analysis...</span>
  </div>
);

function getOrderByMaxValue(data, keys) {
  if (!Array.isArray(data) || data.length === 0) return keys;
  return [...keys].sort((a, b) => {
    const maxA = Math.max(...data.map((d) => (typeof d[a] === "number" ? d[a] : 0)));
    const maxB = Math.max(...data.map((d) => (typeof d[b] === "number" ? d[b] : 0)));
    return maxB - maxA;
  });
}

function CustomTooltip({ active, payload, label, chartType = "bar" }) {
  if (!active || !payload || payload.length === 0) return null;

  const sortedPayload = [...payload].sort((a, b) => {
    const av = typeof a.value === "number" ? a.value : 0;
    const bv = typeof b.value === "number" ? b.value : 0;
    return bv - av;
  });

  return (
    <div className="rounded-lg border border-[#EDF0F2] bg-white px-3 py-2 shadow-md text-[11px]">
      <div className="mb-1 text-[11px] font-medium text-[#323334]">{label}</div>
      {sortedPayload.map((item) => {
        const isTarget = item.dataKey === "targetShare";
        let icon;

        if (isTarget) {
          icon = (
            <svg width="16" height="3" viewBox="0 0 16 3" className="inline">
              <line x1="0" y1="1.5" x2="16" y2="1.5" stroke="#FF0000" strokeWidth="2" strokeDasharray="3,2" />
            </svg>
          );
        } else if (chartType === "line") {
          icon = (
            <svg width="16" height="2" viewBox="0 0 16 2" className="inline">
              <line x1="0" y1="1" x2="16" y2="1" stroke={item.color} strokeWidth="2" />
            </svg>
          );
        } else {
          icon = (
            <span
              className="h-2 w-2 rounded-[2px]"
              style={{ backgroundColor: item.color }}
            />
          );
        }

        return (
          <div
            key={item.dataKey}
            className="mb-0.5 flex items-center justify-between gap-2"
          >
            <div className="flex items-center gap-1.5">
              {icon}
              <span className="text-[#7E8081]">{item.name}</span>
            </div>
            <span className="font-medium text-[#323334]">
              {typeof item.value === "number"
                ? Math.abs(item.value).toLocaleString("en-US", {
                  maximumFractionDigits: 2,
                })
                : item.value}{" "}
              <span className="text-[#98999A]">{yAxisLabelUsd}</span>
            </span>
          </div>
        );
      })}
    </div>
  );
}

function OrderedLegend({ payload, order, hiddenKeys, onToggle, chartType = "bar" }) {
  if (!payload) return null;

  const ordered = [...payload].sort(
    (a, b) => order.indexOf(a.dataKey) - order.indexOf(b.dataKey),
  );

  return (
    <div className="mb-2 flex flex-wrap items-center justify-center gap-4 text-[12px] text-[#98999A]">
      {ordered.map((entry) => {
        const isHidden = hiddenKeys.includes(entry.dataKey);
        return (
          <button
            key={entry.dataKey}
            type="button"
            onClick={() => onToggle(entry)}
            className={`flex items-center gap-1.5 ${isHidden ? "opacity-40" : ""}`}
            title="Click to show/hide"
          >
            <span className="text-[12px]">{entry.value}</span>
            {chartType === "line" ? (
              <svg width="20" height="2" viewBox="0 0 20 2" className="inline">
                <line x1="0" y1="1" x2="20" y2="1" stroke={entry.color || "#9999CC"} strokeWidth="2" />
              </svg>
            ) : (
              <span
                className="h-3 w-3 rounded-[2px]"
                style={{ backgroundColor: entry.color }}
              />
            )}
          </button>
        );
      })}
    </div>
  );
}

const COLOR_POOL = [
  "#80D5FF", // Light Blue
  "#FB752E", // Orange
  "#FFD786", // Light Yellow
  "#FF5579", // Coral/Pink
  "#009B8A", // Teal
  "#CBF3DD", // Mint Green
  "#7C3AED", // Purple
  "#F59E0B", // Amber
  "#10B981", // Emerald
  "#EF4444", // Red
  "#06B6D4", // Cyan
  "#EC4899", // Rose
  "#8B5CF6", // Violet
  "#14B8A6", // Turquoise
  "#F97316", // Orange-Red
  "#6366F1", // Indigo
  "#84CC16", // Lime
  "#0891B2", // Light Cyan
  "#D946EF", // Fuchsia
  "#15803D", // Green
  "#BE123C", // Rose-Red
  "#7C2D12", // Brown
  "#0E7490", // Dark Cyan
  "#5B21B6", // Dark Purple
  "#713F12", // Dark Brown
];

function colorForKey(key) {
  if (key === "others") return "#80D5FF";
  const str = String(key);
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash) ^ str.charCodeAt(i);
  }
  // Use Math.abs and modulo to get a consistent index
  const index = Math.abs(hash) % COLOR_POOL.length;
  return COLOR_POOL[index];
}

function displayName(key) {
  if (key === "others") return "Others";
  return key;
}

function getCountrySeries(block, selectedCountry) {
  if (Array.isArray(block)) return block;
  if (!block || typeof block !== "object") return [];

  // If "Global" or "Average of all countries" is selected, sum all countries
  const isGlobalSelection =
    selectedCountry === "Global" ||
    selectedCountry === "Average of all countries";

  if (isGlobalSelection) {
    // Obtain all country series (except for Global data if they exist)
    const allCountries = Object.keys(block).filter(
      (key) => key !== "Global" && key !== "Average of all countries"
    );

    if (allCountries.length === 0) {
      const maybeSeries = block[selectedCountry];
      return Array.isArray(maybeSeries) ? maybeSeries : [];
    }

    // Obtain all data and group by year
    const yearMap = new Map();

    allCountries.forEach((country) => {
      const countrySeries = block[country];
      if (Array.isArray(countrySeries)) {
        countrySeries.forEach((row) => {
          const year = row.year;
          if (!yearMap.has(year)) {
            yearMap.set(year, {
              year,
              exports: 0,
              imports: 0,
            });
          }
          const yearData = yearMap.get(year);
          yearData.exports += Number(row.exports) || 0;
          yearData.imports += Number(row.imports) || 0;
        });
      }
    });

    // Convert the map to an array sorted by year
    return Array.from(yearMap.values()).sort(
      (a, b) => Number(a.year) - Number(b.year)
    );
  }

  const maybeSeries = block[selectedCountry];
  return Array.isArray(maybeSeries) ? maybeSeries : [];
}

function getProductSeries(block, selectedProduct) {
  if (!block || typeof block !== "object") return [];
  const seriesByProduct = block.seriesByProduct;
  if (!seriesByProduct || typeof seriesByProduct !== "object") return [];
  const maybeSeries = seriesByProduct[selectedProduct];
  return Array.isArray(maybeSeries) ? maybeSeries : [];
}

function extractAnalysisText(resData, fallbackKeys = []) {
  if (!resData || typeof resData !== "object") {
    return "Could not load analysis at this time.";
  }

  if (typeof resData.analysis === "string" && resData.analysis.trim()) {
    return resData.analysis;
  }

  for (const key of fallbackKeys) {
    if (typeof resData[key] === "string" && resData[key].trim()) {
      return resData[key];
    }
  }

  return "Could not load analysis at this time.";
}

function getSelectValue(value) {
  if (value && typeof value === "object" && "value" in value) {
    return value.value;
  }
  return value ?? "";
}

export default function TradeTab({ data, pathway, pathwayId, scenathonId }) {
  const countries = Array.isArray(data?.countries) ? data.countries : [];

  const defaultCountry = useMemo(() => {
    if (countries.includes("Average of all countries")) {
      return "Average of all countries";
    }
    return countries[0] || "";
  }, [countries]);

  const exportProducts = Array.isArray(
    data?.["Exports of commodities by region"]?.products,
  )
    ? data["Exports of commodities by region"].products
    : [];

  const importProducts = Array.isArray(
    data?.["Imports of commodities by region"]?.products,
  )
    ? data["Imports of commodities by region"].products
    : [];

  const defaultExportProduct = exportProducts[0] || "";
  const defaultImportProduct = importProducts[0] || "";

  const [selectedTradeCountry, setSelectedTradeCountry] = useState(defaultCountry);
  const [selectedExportProduct, setSelectedExportProduct] = useState(defaultExportProduct);
  const [selectedImportProduct, setSelectedImportProduct] = useState(defaultImportProduct);

  const [hiddenTradeKeys, setHiddenTradeKeys] = useState([]);
  const [hiddenExportKeys, setHiddenExportKeys] = useState([]);
  const [hiddenImportKeys, setHiddenImportKeys] = useState([]);

  const [tradeAnalysis, setTradeAnalysis] = useState("");
  const [exportsAnalysis, setExportsAnalysis] = useState("");
  const [importsAnalysis, setImportsAnalysis] = useState("");
  const [loadingTradeAnalysis, setLoadingTradeAnalysis] = useState(false);
  const [loadingExportsAnalysis, setLoadingExportsAnalysis] = useState(false);
  const [loadingImportsAnalysis, setLoadingImportsAnalysis] = useState(false);

  useEffect(() => {
    if (!selectedTradeCountry && defaultCountry) {
      setSelectedTradeCountry(defaultCountry);
    } else if (
      selectedTradeCountry &&
      countries.length > 0 &&
      !countries.includes(selectedTradeCountry)
    ) {
      setSelectedTradeCountry(defaultCountry);
    }

    if (!selectedExportProduct && defaultExportProduct) {
      setSelectedExportProduct(defaultExportProduct);
    } else if (
      selectedExportProduct &&
      exportProducts.length > 0 &&
      !exportProducts.includes(selectedExportProduct)
    ) {
      setSelectedExportProduct(defaultExportProduct);
    }

    if (!selectedImportProduct && defaultImportProduct) {
      setSelectedImportProduct(defaultImportProduct);
    } else if (
      selectedImportProduct &&
      importProducts.length > 0 &&
      !importProducts.includes(selectedImportProduct)
    ) {
      setSelectedImportProduct(defaultImportProduct);
    }
  }, [
    countries,
    exportProducts,
    importProducts,
    defaultCountry,
    defaultExportProduct,
    defaultImportProduct,
    selectedTradeCountry,
    selectedExportProduct,
    selectedImportProduct,
  ]);

  const tradeBalanceRaw = useMemo(() => {
    return getCountrySeries(data?.["Trade balance"], selectedTradeCountry);
  }, [data, selectedTradeCountry]);

  const tradeBalanceData = useMemo(() => {
    const safeRows = Array.isArray(tradeBalanceRaw) ? tradeBalanceRaw : [];
    return safeRows.map((d) => ({
      year: d.year,
      exports: Number(d.exports) || 0,
      imports: -(Number(d.imports) || 0),
    }));
  }, [tradeBalanceRaw]);

  const exportsRegionData = useMemo(() => {
    return getProductSeries(
      data?.["Exports of commodities by region"],
      selectedExportProduct,
    );
  }, [data, selectedExportProduct]);

  const importsRegionData = useMemo(() => {
    return getProductSeries(
      data?.["Imports of commodities by region"],
      selectedImportProduct,
    );
  }, [data, selectedImportProduct]);

  const exportRegionKeys = useMemo(() => {
    if (!Array.isArray(exportsRegionData) || exportsRegionData.length === 0) return [];
    const first = exportsRegionData[0];
    if (!first || typeof first !== "object") return [];
    return Object.keys(first).filter((k) => k !== "year" && k !== "Global" && k !== "Average of all countries");
  }, [exportsRegionData]);

  const importRegionKeys = useMemo(() => {
    if (!Array.isArray(importsRegionData) || importsRegionData.length === 0) return [];
    const first = importsRegionData[0];
    if (!first || typeof first !== "object") return [];
    return Object.keys(first).filter((k) => k !== "year" && k !== "Global" && k !== "Average of all countries");
  }, [importsRegionData]);

  const tradeLegendOrder = useMemo(
    () => getOrderByMaxValue(tradeBalanceData, ["imports", "exports"]),
    [tradeBalanceData],
  );

  const exportsLegendOrder = useMemo(
    () => getOrderByMaxValue(exportsRegionData, exportRegionKeys),
    [exportsRegionData, exportRegionKeys],
  );

  const importsLegendOrder = useMemo(
    () => getOrderByMaxValue(importsRegionData, importRegionKeys),
    [importsRegionData, importRegionKeys],
  );

  const countryOptions = useMemo(
    () =>
      countries.map((country) => ({
        value: country,
        label: country,
      })),
    [countries],
  );

  const exportProductOptions = useMemo(
    () =>
      exportProducts.map((product) => ({
        value: product,
        label: product,
      })),
    [exportProducts],
  );

  const importProductOptions = useMemo(
    () =>
      importProducts.map((product) => ({
        value: product,
        label: product,
      })),
    [importProducts],
  );

  useEffect(() => {
    if (!tradeBalanceData.length || !scenathonId || !pathway || !selectedTradeCountry) {
      setTradeAnalysis("");
      setLoadingTradeAnalysis(false);
      return;
    }

    const controller = new AbortController();

    const fetchAnalysis = async () => {
      setLoadingTradeAnalysis(true);
      try {
        const payload = {
          analysisKey: "trade_balance_analysis",
          scope: "country",
          scenathonId,
          pathway,
          country: selectedTradeCountry,
          tradeBalanceData: {
            data: JSON.stringify(tradeBalanceData),
            title: `Trade balance - ${selectedTradeCountry}`,
          },
          context: "trade",
        };

        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/chat/fablegpt_scenathon`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
            signal: controller.signal,
          },
        );

        if (!response.ok) {
          throw new Error(`Analysis API error: ${response.status}`);
        }

        const resData = await response.json();
        setTradeAnalysis(
          extractAnalysisText(resData, ["tradeBalanceAnalysis", "analysis"]),
        );
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Error fetching trade analysis:", error);
          setTradeAnalysis("Could not load analysis at this time.");
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoadingTradeAnalysis(false);
        }
      }
    };

    fetchAnalysis();

    return () => controller.abort();
  }, [tradeBalanceData, pathway, scenathonId, selectedTradeCountry]);

  useEffect(() => {
    if (!exportsRegionData.length || !scenathonId || !pathway || !selectedExportProduct) {
      setExportsAnalysis("");
      setLoadingExportsAnalysis(false);
      return;
    }

    const controller = new AbortController();

    const fetchAnalysis = async () => {
      setLoadingExportsAnalysis(true);
      try {
        const payload = {
          analysisKey: "exports_analysis",
          scope: "product",
          scenathonId,
          pathway,
          product: selectedExportProduct,
          exportsData: {
            data: JSON.stringify(exportsRegionData),
            title: `Exports of commodities by region - ${selectedExportProduct}`,
          },
          context: "trade",
        };

        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/chat/fablegpt_scenathon`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
            signal: controller.signal,
          },
        );

        if (!response.ok) {
          throw new Error(`Analysis API error: ${response.status}`);
        }

        const resData = await response.json();
        setExportsAnalysis(
          extractAnalysisText(resData, ["exportsAnalysis", "analysis"]),
        );
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Error fetching exports analysis:", error);
          setExportsAnalysis("Could not load analysis at this time.");
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoadingExportsAnalysis(false);
        }
      }
    };

    fetchAnalysis();

    return () => controller.abort();
  }, [exportsRegionData, pathway, scenathonId, selectedExportProduct]);

  useEffect(() => {
    if (!importsRegionData.length || !scenathonId || !pathway || !selectedImportProduct) {
      setImportsAnalysis("");
      setLoadingImportsAnalysis(false);
      return;
    }

    const controller = new AbortController();

    const fetchAnalysis = async () => {
      setLoadingImportsAnalysis(true);
      try {
        const payload = {
          analysisKey: "imports_analysis",
          scope: "product",
          scenathonId,
          pathway,
          product: selectedImportProduct,
          importsData: {
            data: JSON.stringify(importsRegionData),
            title: `Imports of commodities by region - ${selectedImportProduct}`,
          },
          context: "trade",
        };

        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/chat/fablegpt_scenathon`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
            signal: controller.signal,
          },
        );

        if (!response.ok) {
          throw new Error(`Analysis API error: ${response.status}`);
        }

        const resData = await response.json();
        setImportsAnalysis(
          extractAnalysisText(resData, ["importsAnalysis", "analysis"]),
        );
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Error fetching imports analysis:", error);
          setImportsAnalysis("Could not load analysis at this time.");
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoadingImportsAnalysis(false);
        }
      }
    };

    fetchAnalysis();

    return () => controller.abort();
  }, [importsRegionData, pathway, scenathonId, selectedImportProduct]);

  const handleLegendClickTrade = (o) => {
    const { dataKey } = o || {};
    if (!dataKey) return;
    setHiddenTradeKeys((prev) =>
      prev.includes(dataKey)
        ? prev.filter((k) => k !== dataKey)
        : [...prev, dataKey],
    );
  };

  const handleLegendClickExports = (o) => {
    const { dataKey } = o || {};
    if (!dataKey) return;
    setHiddenExportKeys((prev) =>
      prev.includes(dataKey)
        ? prev.filter((k) => k !== dataKey)
        : [...prev, dataKey],
    );
  };

  const handleLegendClickImports = (o) => {
    const { dataKey } = o || {};
    if (!dataKey) return;
    setHiddenImportKeys((prev) =>
      prev.includes(dataKey)
        ? prev.filter((k) => k !== dataKey)
        : [...prev, dataKey],
    );
  };

  const hasTradeBalance =
    Array.isArray(tradeBalanceData) && tradeBalanceData.length > 0;
  const hasExports =
    Array.isArray(exportsRegionData) &&
    exportsRegionData.length > 0 &&
    exportRegionKeys.length > 0;
  const hasImports =
    Array.isArray(importsRegionData) &&
    importsRegionData.length > 0 &&
    importRegionKeys.length > 0;

  return (
    <div className="flex w-full flex-col gap-10">
      <section className="flex w-full flex-col gap-4">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <h2 className="text-[18px] font-medium text-[#323334]">
            Commodities Trade
          </h2>

          <div className="w-full max-w-sm">
            <Select
              label="Country"
              value={selectedTradeCountry}
              onChange={(value) => setSelectedTradeCountry(getSelectValue(value))}
              options={countryOptions}
              placeholder="Select country"
              disabled={countryOptions.length === 0}
              className="w-full"
            />
          </div>
        </div>

        <div className="h-[600px] rounded-xl border border-[#EDF0F2] px-6 py-4">
          {hasTradeBalance ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={tradeBalanceData}
                stackOffset="sign"
                margin={{ top: 40, right: 30, left: 60, bottom: 30 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="year"
                  interval={0}
                  angle={-45}
                  textAnchor="end"
                  height={70}
                  tick={{ fill: "#98999A", fontSize: 12 }}
                  tickMargin={10}
                >
                  <Label
                    value={xAxisLabelYear}
                    position="insideBottom"
                    offset={-20}
                    style={{ fill: "#98999A", fontSize: 15 }}
                  />
                </XAxis>

                <YAxis
                  width={60}
                  tick={{ fill: "#98999A", fontSize: 12 }}
                  tickFormatter={(v) => {
                    const n = Number(v);
                    if (!Number.isFinite(n)) return "";
                    const abs = Math.abs(n);
                    const inMillions = abs >= 1_000_000 ? abs / 1_000_000 : abs;
                    const maxFD = inMillions < 10 ? 2 : 1;
                    return inMillions.toLocaleString("en-US", {
                      maximumFractionDigits: maxFD,
                    });
                  }}
                >
                  <Label
                    value={yAxisLabelUsd}
                    angle={-90}
                    position="left"
                    dx={yAxisDxUsd}
                    style={{ fill: "#98999A", fontSize: 15 }}
                  />
                </YAxis>

                <Tooltip
                  content={(props) => <CustomTooltip {...props} chartType="bar" />}
                  cursor={{ fill: "rgba(0,0,0,0.03)" }}
                />

                <Legend
                  verticalAlign="top"
                  align="left"
                  wrapperStyle={{ top: 0, left: 0 }}
                  content={(props) => (
                    <OrderedLegend
                      {...props}
                      order={tradeLegendOrder}
                      hiddenKeys={hiddenTradeKeys}
                      onToggle={handleLegendClickTrade}
                      chartType="bar"
                    />
                  )}
                />

                <Bar
                  dataKey="imports"
                  name="Imports"
                  fill="#80D5FF"
                  stackId="stack"
                  hide={hiddenTradeKeys.includes("imports")}
                />
                <Bar
                  dataKey="exports"
                  name="Exports"
                  fill="#FB752E"
                  stackId="stack"
                  hide={hiddenTradeKeys.includes("exports")}
                />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-[#7E8081]">
              No trade balance data available.
            </div>
          )}
        </div>

        {/* <div className="w-full min-h-[100px]">
          {loadingTradeAnalysis ? (
            <LoadingAnalysisHelper />
          ) : (
            <NativeAnalysis content={tradeAnalysis} />
          )}
        </div> */}
      </section>

      <section className="flex w-full flex-col gap-4">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <h2 className="text-[18px] font-medium text-[#323334]">
            Exports of commodities by region
          </h2>

          <div className="w-full max-w-sm">
            <Select
              label="Product"
              value={selectedExportProduct}
              onChange={(value) => setSelectedExportProduct(getSelectValue(value))}
              options={exportProductOptions}
              placeholder="Select product"
              disabled={exportProductOptions.length === 0}
              className="w-full"
            />
          </div>
        </div>

        <div className="h-[600px] rounded-xl border border-[#EDF0F2] px-6 py-4">
          {hasExports ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={exportsRegionData}
                stackOffset="none"
                margin={{ top: 40, right: 30, left: 60, bottom: 30 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="year"
                  interval={0}
                  angle={-45}
                  textAnchor="end"
                  height={70}
                  tick={{ fill: "#98999A", fontSize: 12 }}
                  tickMargin={10}
                >
                  <Label
                    value={xAxisLabelYear}
                    position="insideBottom"
                    offset={-20}
                    style={{ fill: "#98999A", fontSize: 15 }}
                  />
                </XAxis>

                <YAxis
                  width={50}
                  tick={{ fill: "#98999A", fontSize: 12 }}
                // tickFormatter={(v) =>
                //   Number(v / 1_000_000).toLocaleString("es-ES", {
                //     maximumFractionDigits: 100,
                //   })
                // }
                >
                  <Label
                    value={yAxisLabelUsd}
                    angle={-90}
                    position="left"
                    dx={yAxisDxUsd}
                    style={{ fill: "#98999A", fontSize: 15 }}
                  />
                </YAxis>

                <Tooltip
                  content={(props) => <CustomTooltip {...props} chartType="bar" />}
                  cursor={{ fill: "rgba(0,0,0,0.03)" }}
                />

                <Legend
                  verticalAlign="top"
                  align="left"
                  wrapperStyle={{ top: 0, left: 0 }}
                  content={(props) => (
                    <OrderedLegend
                      {...props}
                      order={exportsLegendOrder}
                      hiddenKeys={hiddenExportKeys}
                      onToggle={handleLegendClickExports}
                      chartType="bar"
                    />
                  )}
                />

                {exportRegionKeys.map((k, index) => (
                  <Bar
                    key={k}
                    dataKey={k}
                    stackId="a"
                    fill={COLOR_POOL[index % COLOR_POOL.length]}
                    name={displayName(k)}
                    hide={hiddenExportKeys.includes(k)}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-[#7E8081]">
              No export data by region available.
            </div>
          )}
        </div>

        {/* <div className="w-full min-h-[100px]">
          {loadingExportsAnalysis ? (
            <LoadingAnalysisHelper />
          ) : (
            <NativeAnalysis content={exportsAnalysis} />
          )}
        </div> */}
      </section>

      <section className="flex w-full flex-col gap-4">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <h2 className="text-[18px] font-medium text-[#323334]">
            Imports of commodities by region
          </h2>

          <div className="w-full max-w-sm">
            <Select
              label="Product"
              value={selectedImportProduct}
              onChange={(value) => setSelectedImportProduct(getSelectValue(value))}
              options={importProductOptions}
              placeholder="Select product"
              disabled={importProductOptions.length === 0}
              className="w-full"
            />
          </div>
        </div>

        <div className="h-[600px] rounded-xl border border-[#EDF0F2] px-6 py-4">
          {hasImports ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={importsRegionData}
                stackOffset="none"
                margin={{ top: 40, right: 30, left: 60, bottom: 30 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="year"
                  interval={0}
                  angle={-45}
                  textAnchor="end"
                  height={70}
                  tick={{ fill: "#98999A", fontSize: 12 }}
                  tickMargin={10}
                >
                  <Label
                    value={xAxisLabelYear}
                    position="insideBottom"
                    offset={-20}
                    style={{ fill: "#98999A", fontSize: 15 }}
                  />
                </XAxis>

                <YAxis
                  width={50}
                  tick={{ fill: "#98999A", fontSize: 12 }}
                // tickFormatter={(v) =>
                //   Number(v / 1_000_000).toLocaleString("es-ES", {
                //     maximumFractionDigits: 1,
                //   })
                // }
                >
                  <Label
                    value={yAxisLabelUsd}
                    angle={-90}
                    position="left"
                    dx={yAxisDxUsd}
                    style={{ fill: "#98999A", fontSize: 15 }}
                  />
                </YAxis>

                <Tooltip
                  content={(props) => <CustomTooltip {...props} chartType="bar" />}
                  cursor={{ fill: "rgba(0,0,0,0.03)" }}
                />

                <Legend
                  verticalAlign="top"
                  align="left"
                  wrapperStyle={{ top: 0, left: 0 }}
                  content={(props) => (
                    <OrderedLegend
                      {...props}
                      order={importsLegendOrder}
                      hiddenKeys={hiddenImportKeys}
                      onToggle={handleLegendClickImports}
                      chartType="bar"
                    />
                  )}
                />

                {importRegionKeys.map((k, index) => (
                  <Bar
                    key={k}
                    dataKey={k}
                    stackId="a"
                    fill={COLOR_POOL[index % COLOR_POOL.length]}
                    name={displayName(k)}
                    hide={hiddenImportKeys.includes(k)}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-[#7E8081]">
              No import data by region available.
            </div>
          )}
        </div>

        {/* <div className="w-full min-h-[100px]">
          {loadingImportsAnalysis ? (
            <LoadingAnalysisHelper />
          ) : (
            <NativeAnalysis content={importsAnalysis} />
          )}
        </div> */}
      </section>
    </div>
  );
}